/**
 * @fileoverview Shared local-first ranker + Dexie reader for the violation
 *               code library. Powers the cross-app `ViolationPicker`
 *               component (system-web template builder, tenant-web template
 *               builder + violations module, future system/tenant grid row
 *               actions).
 * @module local-db/services/violationPickerService
 *
 * Responsibilities:
 * - Provide a deterministic substring-based ranker for `ViolationIndexEntry`
 *   records.
 * - Provide thin Dexie reader helpers (active code sets + violations) that
 *   the picker UI subscribes to via `liveQuery`.
 *
 * Dependencies:
 * - dexie (passed in by the caller)
 *
 * Architecture:
 * - Local-first. The picker UI passes its app's Dexie handle; this service
 *   never makes API calls and never falls back to mock data.
 * - The ranker is pure and exported so AI smart-search (Phase 5) can layer
 *   on top of it without modifying UI callers.
 *
 * Notes:
 * - The ranker is the canonical version of the legacy `rankViolationEntries`
 *   that lives in mobile-pwa's local service. New callers should use this
 *   shared implementation.
 */

import { liveQuery, type Observable } from 'dexie';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract ViolationCodeSetEntry
 *
 * Purpose:
 * - Minimal local-shape of a synced `violationCodeSets` row that the picker
 *   UI consumes for code-set selection.
 *
 * Fields:
 * - _id, name, description, source, isActive, _deleted (tombstone).
 *
 * Rules:
 * - Inactive or tombstoned sets are filtered before reaching the UI.
 *
 * Sync:
 * - Server-authoritative; clients read.
 */
export interface ViolationCodeSetEntry {
  _id: string;
  name: string;
  description?: string;
  source: 'icc' | 'nfpa' | 'custom' | string;
  isActive: boolean;
  _deleted?: boolean;
}

/**
 * @contract ViolationIndexEntry
 *
 * Purpose:
 * - Flat searchable shape of a synced `violationIndex` row for picker UIs.
 *
 * Fields:
 * - _id, codeSetId, code, title, codeReference, contentSnippet,
 *   chapterOrdinal, chapterTitle, source, category, isActive, _deleted.
 *
 * Rules:
 * - Inactive or tombstoned rows MUST be filtered before ranking.
 *
 * Sync:
 * - Mirrored to clients via tenant sync.
 */
export interface ViolationIndexEntry {
  _id: string;
  codeSetId: string;
  code: string;
  title: string;
  codeReference?: string;
  contentSnippet?: string;
  bodyText?: string;
  displayText?: string;
  searchText?: string;
  rawNodeId?: string;
  rawRootId?: string;
  parentViolationId?: string;
  hierarchyPath?: Array<{ id: string; code: string; title: string }>;
  depth?: number;
  sortKey?: string;
  nodeType?: string;
  chapterOrdinal?: string;
  chapterTitle?: string;
  source: string;
  category?: string;
  isActive: boolean;
  _deleted?: boolean;
}

/**
 * @contract ViolationChapterSectionEntry
 *
 * Purpose:
 * - Represents one nested section node within a local violation chapter.
 *
 * Fields:
 * - xmlId: Stable source node identifier.
 * - ordinal: Human-facing section number when available.
 * - label: Source label.
 * - title: Human-facing heading.
 * - violationId: Linked violation index/source node id.
 *
 * Rules:
 * - Raw vendor identifiers are internal fallbacks, not preferred display text.
 *
 * Sync:
 * - Read-only local Dexie data synced through tenant replication.
 */
export interface ViolationChapterSectionEntry {
  xmlId?: string;
  ordinal?: string;
  label?: string;
  title?: string;
  contentText?: string;
  displayText?: string;
  description?: string;
  depth?: number;
  parentXmlId?: string;
  violationId?: string;
  sectionCode?: string;
  parentViolationId?: string;
  sortKey?: string;
  isSelectable?: boolean;
}

/**
 * @contract ViolationChapterEntry
 *
 * Purpose:
 * - Represents one local chapter document for hierarchical browse display.
 *
 * Fields:
 * - _id: Local chapter document identifier.
 * - codeSetId: Parent code set identifier.
 * - chapterId: Source chapter/root identifier.
 * - ordinal: Human-facing chapter number.
 * - title: Human-facing chapter title.
 * - sections: Nested section records for this chapter.
 *
 * Rules:
 * - Browse mode should use this structure before falling back to flat index grouping.
 *
 * Sync:
 * - Read-only local Dexie data synced through tenant replication.
 */
export interface ViolationChapterEntry {
  _id: string;
  codeSetId: string;
  chapterId: string;
  ordinal?: string;
  title?: string;
  sections?: ViolationChapterSectionEntry[];
  _deleted?: boolean;
}

/**
 * @contract ViolationRankOptions
 *
 * Purpose:
 * - Controls picker ranker output shaping.
 *
 * Fields:
 * - limit:      Maximum results to return (default 50).
 * - codeSetIds: Optional restriction to a subset of code sets.
 *
 * Rules:
 * - When `codeSetIds` is empty, ranker considers every active entry.
 *
 * Sync:
 * - N/A — pure ranker.
 */
export interface ViolationRankOptions {
  limit?: number;
  codeSetIds?: string[];
}

/**
 * @contract ViolationRankFn
 *
 * Purpose:
 * - Optional injection seam used by callers (Phase 5 AI ranker fallback).
 *
 * Rules:
 * - Implementations MUST be pure and synchronous.
 */
export type ViolationRankFn = (
  entries: ViolationIndexEntry[],
  query: string,
  options?: ViolationRankOptions,
) => ViolationIndexEntry[];

/**
 * @contract ViolationAsyncRankFn
 *
 * Purpose:
 * - Async injection seam used by Phase 5 AI smart-search rankers. Receives
 *   the local Dexie entries and returns the same entries reordered (and
 *   optionally trimmed) by a remote semantic ranker.
 *
 * Rules:
 * - Implementations MUST honor `signal.aborted` and return promptly when
 *   the picker query changes mid-flight.
 * - Implementations MUST gracefully fall back to lexical ranking when the
 *   remote ranker is unavailable (offline, 5xx, timeout). Returning the
 *   raw lexical result is acceptable.
 * - The returned array MUST be a subset of `entries` (no fabricated rows).
 *
 * Sync:
 * - Calls a server-side semantic ranker; never persists to Dexie.
 */
export type ViolationAsyncRankFn = (
  entries: ViolationIndexEntry[],
  query: string,
  options: ViolationRankOptions & { signal?: AbortSignal },
) => Promise<ViolationIndexEntry[]>;

// ---------------------------------------------------------------------------
// Ranker
// ---------------------------------------------------------------------------

function normalize(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * @process ScoreViolationEntry
 *
 * Steps:
 * 1. Normalize all searchable fields.
 * 2. Require every query token to appear somewhere across the searchable text.
 * 3. Award points by field rank (code > reference > title > category > chapter > snippet).
 *
 * Inputs:
 * - entry, query string.
 *
 * Outputs:
 * - Numeric score; 0 means filter out.
 *
 * Failure Handling:
 * - N/A — pure.
 *
 * Idempotency:
 * - REQUIRED.
 */
function scoreEntry(entry: ViolationIndexEntry, query: string): number {
  const q = normalize(query);
  if (!q) return 1;

  const code = normalize(entry.code);
  const title = normalize(entry.title);
  const ref = normalize(entry.codeReference);
  const snippet = normalize(entry.contentSnippet);
  const bodyText = normalize(entry.bodyText);
  const displayText = normalize(entry.displayText);
  const richSearchText = normalize(entry.searchText);
  const category = normalize(entry.category);
  const chapter = normalize(entry.chapterTitle);
  const searchable = [code, title, ref, snippet, bodyText, displayText, richSearchText, category, chapter].join(' ');
  const tokens = q.split(' ').filter(Boolean);

  if (!tokens.every((token) => searchable.includes(token))) return 0;

  let score = 1;
  if (code === q) score += 1000;
  if (code.startsWith(q)) score += 800;
  if (code.includes(q)) score += 600;
  if (ref.includes(q)) score += 450;
  if (title === q) score += 400;
  if (title.startsWith(q)) score += 300;
  if (title.includes(q)) score += 220;
  if (category.includes(q)) score += 140;
  if (chapter.includes(q)) score += 120;
  if (snippet.includes(q)) score += 90;
  if (displayText.includes(q)) score += 70;
  if (bodyText.includes(q)) score += 60;
  if (richSearchText.includes(q)) score += 55;

  for (const token of tokens) {
    if (code.includes(token)) score += 80;
    if (ref.includes(token)) score += 55;
    if (title.includes(token)) score += 45;
    if (category.includes(token)) score += 25;
    if (chapter.includes(token)) score += 20;
    if (snippet.includes(token)) score += 12;
    if (displayText.includes(token)) score += 10;
    if (bodyText.includes(token)) score += 8;
    if (richSearchText.includes(token)) score += 7;
  }

  return score;
}

/**
 * @process RankViolationEntries
 *
 * Steps:
 * 1. Drop inactive and tombstoned rows.
 * 2. Apply optional `codeSetIds` filter.
 * 3. Score remaining rows; drop zero-score; sort by score desc, then
 *    natural-numeric `code` ascending for stable display.
 * 4. Cap to `limit`.
 *
 * Inputs:
 * - Array of index entries, query string, options.
 *
 * Outputs:
 * - Sorted, filtered array of entries.
 *
 * Failure Handling:
 * - Returns [] for empty/invalid inputs.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function rankViolationEntries(
  entries: ViolationIndexEntry[],
  query: string,
  options: ViolationRankOptions = {},
): ViolationIndexEntry[] {
  const limit = options.limit ?? 50;
  const allowedSets = options.codeSetIds && options.codeSetIds.length > 0
    ? new Set(options.codeSetIds)
    : null;

  return entries
    .filter((entry) => entry.isActive && !entry._deleted)
    .filter((entry) => (allowedSets ? allowedSets.has(entry.codeSetId) : true))
    .map((entry) => ({ entry, score: scoreEntry(entry, query) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.entry.code.localeCompare(b.entry.code, undefined, { numeric: true });
    })
    .map((row) => row.entry)
    .slice(0, limit);
}

// ---------------------------------------------------------------------------
// Dexie readers
// ---------------------------------------------------------------------------

/**
 * @process WatchActiveViolationCodeSets
 *
 * Steps:
 * 1. Subscribe via Dexie `liveQuery` to the `violationCodeSets` table.
 * 2. Filter out inactive + tombstoned rows.
 * 3. Sort by name for stable picker rendering.
 *
 * Inputs:
 * - db: Dexie database with a `violationCodeSets` table.
 *
 * Outputs:
 * - Observable<ViolationCodeSetEntry[]>.
 *
 * Failure Handling:
 * - liveQuery surfaces errors via subscription onError.
 *
 * Idempotency:
 * - REQUIRED.
 */
// ---------------------------------------------------------------------------
// Step B.0 gap-closure (2026-05-01) / Review Finding 3:
// The tenant Dexie schema uses snake_case tables
// (`violation_code_sets` + `violation_index`). The picker previously read
// camelCase names and threw MissingTableError on every tenant. These helper
// accessors resolve the correct table name and return a safe empty result
// when the table is absent (e.g. system-web Dexie which has no violation
// schema — any accidental wiring there becomes a no-op instead of a crash).
// ---------------------------------------------------------------------------

const VIOLATION_CODE_SETS_TABLE = 'violation_code_sets';
const VIOLATION_INDEX_TABLE = 'violation_index';
const VIOLATION_CHAPTERS_TABLE = 'violation_chapters';

function resolveTable(db: any, name: string): any | null {
  try {
    const rawTable = db?.table?.(name);
    if (rawTable) return rawTable;
    const wrappedTable = db?.[name]?.table;
    if (wrappedTable) return wrappedTable;
    const collectionTable = db?.collections?.[name]?.table;
    return collectionTable ?? null;
  } catch {
    return null;
  }
}

export function watchActiveViolationCodeSets(db: any): Observable<ViolationCodeSetEntry[]> {
  return liveQuery(async () => {
    const table = resolveTable(db, VIOLATION_CODE_SETS_TABLE);
    if (!table) return [] as ViolationCodeSetEntry[];
    const rows = await table.toArray() as ViolationCodeSetEntry[];
    return rows
      .filter((cs) => cs.isActive && !cs._deleted)
      .sort((a, b) => a.name.localeCompare(b.name));
  });
}

/**
 * @process WatchViolationIndex
 *
 * Steps:
 * 1. Subscribe via Dexie `liveQuery` to the `violationIndex` table.
 * 2. Optionally restrict to a set of `codeSetIds`.
 * 3. Drop inactive + tombstoned rows.
 *
 * Inputs:
 * - db: Dexie database, optional codeSetIds filter.
 *
 * Outputs:
 * - Observable<ViolationIndexEntry[]>.
 *
 * Failure Handling:
 * - Subscription error surfaced through Dexie.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function watchViolationIndex(
  db: any,
  codeSetIds?: string[],
): Observable<ViolationIndexEntry[]> {
  return liveQuery(async () => {
    const table = resolveTable(db, VIOLATION_INDEX_TABLE);
    if (!table) return [] as ViolationIndexEntry[];
    const allowed = codeSetIds && codeSetIds.length > 0 ? new Set(codeSetIds) : null;
    let collection = table.toCollection();
    if (allowed && allowed.size === 1) {
      // Single-set queries use the indexed `codeSetId` for efficiency.
      const only = Array.from(allowed)[0];
      collection = table.where('codeSetId').equals(only);
    }
    const rows = await collection.toArray() as ViolationIndexEntry[];
    return rows
      .filter((v) => v.isActive && !v._deleted)
      .filter((v) => (allowed ? allowed.has(v.codeSetId) : true));
  });
}

/**
 * @process CountViolationsByCodeSet
 *
 * Steps:
 * 1. Subscribe to the active violation index.
 * 2. Tally counts grouped by `codeSetId` for picker badge display.
 *
 * Inputs:
 * - db.
 *
 * Outputs:
 * - Observable<Record<codeSetId, number>>.
 *
 * Failure Handling:
 * - Subscription error surfaced through Dexie.
 *
 * Idempotency:
 * - REQUIRED.
 */
/**
 * @process WatchViolationChapters
 *
 * Steps:
 * 1. Subscribe via Dexie `liveQuery` to the `violation_chapters` table.
 * 2. Optionally restrict to selected code sets.
 * 3. Drop tombstoned rows while preserving source order.
 *
 * Inputs:
 * - db: Dexie database, optional codeSetIds filter.
 *
 * Outputs:
 * - Observable<ViolationChapterEntry[]>.
 *
 * Failure Handling:
 * - Returns [] when no local chapter table exists.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function watchViolationChapters(
  db: any,
  codeSetIds?: string[],
): Observable<ViolationChapterEntry[]> {
  return liveQuery(async () => {
    const table = resolveTable(db, VIOLATION_CHAPTERS_TABLE);
    if (!table) return [] as ViolationChapterEntry[];
    const allowed = codeSetIds && codeSetIds.length > 0 ? new Set(codeSetIds) : null;
    let collection = table.toCollection();
    if (allowed && allowed.size === 1) {
      const only = Array.from(allowed)[0];
      collection = table.where('codeSetId').equals(only);
    }
    const rows = await collection.toArray() as ViolationChapterEntry[];
    return rows
      .filter((chapter) => !chapter._deleted)
      .filter((chapter) => (allowed ? allowed.has(chapter.codeSetId) : true));
  });
}
export function watchViolationCountsByCodeSet(db: any): Observable<Record<string, number>> {
  return liveQuery(async () => {
    const table = resolveTable(db, VIOLATION_INDEX_TABLE);
    if (!table) return {} as Record<string, number>;
    const rows = await table.toArray() as ViolationIndexEntry[];
    const counts: Record<string, number> = {};
    for (const v of rows) {
      if (!v.isActive || v._deleted) continue;
      counts[v.codeSetId] = (counts[v.codeSetId] ?? 0) + 1;
    }
    return counts;
  });
}
